import { useCallback, useEffect, useState } from 'react'
import { backendClient } from '../../interactors/clients/client'
import { useAccountsStore } from '../../store/AccountsStore'

/**
 * `missing` means the organization genuinely has no usable Stripe account. `error` means the
 * question could not be answered -- the request failed -- which is not the same thing, and must not
 * be used to tell an organization that it has not set up payments.
 */
export type StripeConnectStatus = 'loading' | 'connected' | 'missing' | 'error'

export const useStripeConnectStatus = (enabled = true): { status: StripeConnectStatus; refresh: () => void } => {
  const [status, setStatus] = useState<StripeConnectStatus>('loading')
  const [refreshIndex, setRefreshIndex] = useState(0)
  const stripeAccountId = useAccountsStore((state) => state.connectedCustomer?.stripeAccountId)

  const refresh = useCallback(() => {
    setRefreshIndex((prev) => prev + 1)
  }, [])

  useEffect(() => {
    if (!enabled) {
      return
    }
    let isMounted = true
    setStatus('loading')

    const fetchStatus = async () => {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isLocalhost && stripeAccountId) {
        setStatus('connected')
        return
      }

      try {
        const response = await backendClient.get('/customers/stripe-connect-status')
        if (!isMounted) return
        const connected = response.data === true || response.data === 'valid'
        setStatus(connected ? 'connected' : 'missing')
      } catch (error) {
        if (!isMounted) return
        setStatus('error')
      }
    }

    fetchStatus()

    return () => {
      isMounted = false
    }
  }, [enabled, refreshIndex, stripeAccountId])

  return { status, refresh }
}
